import { storage } from '@abyss/web/tools/storage';
import { find } from 'lodash';
import { Dispatch, SetStateAction, useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { getMemberRelvancySpecialityRollupCodes } from '../../../../api/src/utils/specialityUtils/specialityUtils';
import {
  CareCategories,
  ClientNames,
  Constants,
  CoverageTypesCodes,
  GQL_API_NAMES,
  ProviderTypes,
  ReverseCoverageTypesCodes,
} from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { PortalName } from '../../common/Portal';
import { isMnrMember } from '../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../context/PortalContext';
import { isMissingParameters } from '../../errors/helpers';
import { getLanguage } from '../../frontends/ProviderSearch/context/Internationalization/helpers';
import { useCurrentMemberIsPreEffective } from '../../hooks/useCurrentMemberIsPreEffective';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { CostCareCategory, CustomPreferredPolicy } from '../../models/Lagoon';
import { ProviderType } from '../../models/ProviderDetails';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../store/useDetailsStore';
import { DetailsStore } from '../../store/useDetailsStore/detailsStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import {
  conditionalValueRender,
  isPolicyIdPresentForAOESuppression,
} from '../../utils/common';
import { getAiBioEligibility } from '../../utils/getAiBioEligibility.utils';
import { getCarePathCategoriesSortOrder } from '../../utils/providerDetails.utils';
import {
  getLob,
  getMemberHealthCoverageType,
  getProviderIds,
  getRecommendationPayload,
  getSortByValue,
} from '../../utils/providerSearch.utils';
import {
  getAdditionalAttributes,
  getClaimOrEligibilitySystemTypeCode,
  getCoverageEffDateRange,
  getCoverageTypes,
  getCurrentMember,
  getIsCustomPreferredPolicy,
  getIsPolicyIDRequired,
  getMemberPlanYear,
  getNetworkIdsForPES,
  getPlanVariationCode,
  getPolicyNumber,
  getRulesPackageKey,
  getSitusState,
  isPlanVariationCodeRequired,
  useIsEligibleForStandaloneDental,
} from '../../utils/user.utils';
import { useFeatureFlag } from '../useFeatureFlag';
import { useGeoLocationStorage } from '../useGeoLocationStorage';
import { useLagoon } from '../useLagoon';
import { useProviderDetailsSearch } from '../useProviderDetailsSearch';
import { useSmartChoice } from '../useSmartChoice';

export const useProviderDetails = (
  variables,
  setSelectedPCP?: Dispatch<SetStateAction<boolean>>
) => {
  const locale = getLanguage()?.code || 'en';
  const {
    providerType,
    isOHBS,
    locationId,
    shouldGetHGData,
    suppressedVirtualVisitPolicyIds,
    virtualOnlyLocationInd,
  } = variables;
  let { providerId } = variables;
  const { coverageTypeFromStore, storeLob } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageTypeFromStore: state.coverageType,
      storeLob: state.lob,
    }))
  );
  const [searchParams] = useSearchParams();
  const searchFromTypeAhead = searchParams.get('searchFromTypeAhead');
  const reciprocityIdFromParams = searchParams.get('reciprocityId');
  const networkId = searchParams.get('networkId') || null;
  const coverageTypeFromParams = searchParams.get('coverageType');
  const { preferredSpecialityCode } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      preferredSpecialityCode:
        state.typeaheadSearchStore.preferredSpecialityCode,
    }))
  );
  const { organizationCode } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      organizationCode: state.detailsState.organizationCode,
    }))
  );
  const suppressPremium = searchParams.get('suppressPremium');
  const suppressCostEfficiency = searchParams.get('suppressCostEfficiency');
  const uniqueIdentifier = searchParams.get('uniqueIdentifier');

  const { longitude, latitude, stateCode } = useGeoLocationStorage();

  const currentMember = getCurrentMember();
  const isEligibleForStandaloneDental = useIsEligibleForStandaloneDental();
  const { portalData } = useContext(PortalContext);
  const isLawwPortal =
    portalData?.portalName === PortalName.LAWW ||
    portalData?.portalName === PortalName.LAWW_GUEST;
  const isGuestUser =
    portalData?.portalConfig?.isGuestUser ||
    storage.session.get(Constants.STORAGE_KEYS.SESSION.IS_GUEST);

  const [providerDetails, getProviderDetails] = useProviderDetailsSearch(
    {},
    providerType
  );

  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();

  const [
    uspTierOneFlag,
    enableTier1FromMemberRPK,
    uspToggleFlag,
    isFutureTerminationDemo,
    enableMockProviderIdsForPreEffective,
    enableTierOneMockProviderIds,
    shouldSuppressEAPFromDetailsPage,
    isProviderIdWithAddressId,
    enableEmployeeAssistanceProgramAcceptedFilter,
    isNewPositionsForSmartChoiceEnabled,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.USP_TIER1_ENABLE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIER1_FROM_MEMBER_RPK,
    ConstantsLagoon.FEATURE_FLAGS.USP_ENABLE,
    ConstantsLagoon.FEATURE_FLAGS.FUTURE_TERMINATION_DEMO,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MOCK_PROVIDER_IDS_FOR_PRE_EFFECTIVE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIER_ONE_MOCK_PROVIDER_IDS,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_EAP_FROM_DETAILS_PAGE,
    ConstantsLagoon.FEATURE_FLAGS.PROVIDER_ID_WITH_ADDRESS_ID,
    ConstantsLagoon.FEATURE_FLAGS
      .ENABLE_EMPLOYEE_ASSISTANCE_PROGRAM_ACCEPTED_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NEW_POSITIONS_FOR_SMART_CHOICE,
  ]);

  // Check AI Bio eligibility (includes feature flag check)
  const policyNumber = getPolicyNumber(currentMember, coverageTypeFromStore);
  const requestAiBio = getAiBioEligibility(policyNumber, stateCode);

  const allConfigs = useLagoon('config')();
  const featureSuppression = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_SUPPRESSION
  )();
  const suppressedAOECodesRow = find(featureSuppression, {
    DNT_feature:
      ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.GENDER_AFFIRMING_AOE_CODES,
    active: true,
  });
  const terminationProviderIdsDemo = allConfigs?.find(
    (row) => row.key === 'TERMINATION_PROVIDER_IDS_DEMO'
  )?.value;

  const futureStartDateProviderIds = allConfigs?.find(
    (row) => row.key === 'FUTURE_START_PROVIDERIDS'
  )?.value;
  const tierOneMockProviderIds = allConfigs?.find(
    (row) => row.key === 'TIER1_MOCK_PROVIDERIDS'
  )?.value;
  const acoMockProviderIds = allConfigs?.find(
    (row) => row.key === 'ACO_MOCK_PROVIDERIDS'
  )?.value;
  const premiumCareMockProviderIds = allConfigs?.find(
    (row) => row.key === 'PREMIUM_MOCK_PROVIDERIDS'
  )?.value;
  const mnrFutureMockProviderIds = allConfigs?.find(
    (row) => row.key === 'MNR_MOCK_PROVIDERIDS'
  )?.value;

  const customPreferredPolicies: CustomPreferredPolicy[] = useLagoon(
    'custom-preferred-policies'
  )();
  const costCareCategories: CostCareCategory[] = useLagoon(
    'cost-care-categories'
  )();

  const tierBenefits = useLagoon('tier-benefit')();
  const facilityAttributes = useLagoon('facility-attributes')();
  const additionalAttributes = useLagoon('additional-attributes')();
  const category = facilityAttributes?.find(
    (row) => row?.facilitySubCategoryCode === organizationCode?.[0]
  );

  const defaultSearchRadius = allConfigs?.find(
    (row) => row.key === 'DEFAULT_SEARCH_RADIUS'
  )?.value;

  const isPractitioner =
    providerType?.toLowerCase() == ProviderTypes.PRACTITIONER;

  const coverageType = coverageTypeFromParams || coverageTypeFromStore;
  const smartChoiceEligibility = useSmartChoice();
  const isSmartChoiceFEEnabled =
    searchParams.get('chipValue') === CareCategories.ALL
      ? smartChoiceEligibility.isAvailable
      : smartChoiceEligibility.isEnabled;
  const isNamedSearch = searchParams.get('isNamedSearch') === 'true';
  const isSmartChoice = isSmartChoiceFEEnabled && !isNamedSearch;
  const chipValue = searchParams.get('chipValue');
  const isSmartChoiceEnabled =
    (isPractitioner ||
      (providerType?.toLowerCase() === ProviderTypes.ORGANIZATION &&
        chipValue === CareCategories.FACILITIES)) &&
    isSmartChoice &&
    !isNamedSearch;
  const sortByValue = getSortByValue(isSmartChoiceEnabled);
  const {
    demographics,
    lineOfBusiness: memberLob,
    population,
    policyNumber: policyId,
    isIFPEarlyPay,
  } = currentMember;

  const lob = getLob(memberLob, storeLob);
  const isMnr = isMnrMember(lob);
  const { includeSpecialityRollupCodes } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      includeSpecialityRollupCodes:
        state.typeaheadSearchStore.includeSpecialityRollupCodes,
    }))
  );
  const preferenceData = useStore(StoreKeys?.RECOMMENDATION_PREFERENCES);
  let storedRecommendationPreferences = {};
  if (includeSpecialityRollupCodes) {
    storedRecommendationPreferences = getRecommendationPayload(
      includeSpecialityRollupCodes
    );
  } else {
    const memberRelevencyCodes = getMemberRelvancySpecialityRollupCodes({
      memberDemographics: {
        dateOfBirth: demographics?.dateOfBirth,
        gender: demographics?.gender,
      },
      providerType,
      lob,
      population,
      useNewAgeAndGenderRollups: true,
      pcpIndicator: demographics?.primaryCare?.providerID ? true : false,
    });
    storedRecommendationPreferences =
      getRecommendationPayload(memberRelevencyCodes);
  }

  const recommendationPreferences = {
    ...storedRecommendationPreferences,
    llmCodes: searchParams.get('llmCodes') || '',
    ...(searchParams.get('providerTierDesignation') === 'NA' && {
      tieredPlan: searchParams.get('providerTierDesignation'),
    }),
    recommendationRadius: searchParams.get('searchRadius')
      ? Number(`${searchParams.get('searchRadius')}`)
      : searchParams.get('recommendationRadius')
      ? Number(searchParams.get('recommendationRadius'))
      : Number(defaultSearchRadius),
    ...(suppressPremium && { suppressPremium }),
    ...(suppressCostEfficiency && { suppressCostEfficiency }),
    ...(uniqueIdentifier && { uniqueIdentifier }),
    billingCode: '',
  };
  const reciprocityId =
    reciprocityIdFromParams ||
    networkId ||
    (isOHBS
      ? '100'
      : getNetworkIdsForPES(currentMember, coverageType, featureFlags));

  const situsState = getSitusState(currentMember, lob);

  const isCustomPreferredPolicy: boolean = getIsCustomPreferredPolicy(
    customPreferredPolicies,
    policyId,
    reciprocityId,
    situsState
  );

  const medicalCoverage = getMemberHealthCoverageType(
    currentMember?.eligibility,
    CoverageTypesCodes.MEDICAL
  );

  const isPreEffective = useCurrentMemberIsPreEffective();
  const currentMemberEffectiveDate = getCoverageEffDateRange(
    currentMember,
    coverageType
  ).startDate;

  const planVariationCode = getPlanVariationCode(currentMember, coverageType);

  const planVariationCodeRequired = isPlanVariationCodeRequired(
    currentMember?.lineOfBusiness,
    currentMember?.population,
    portalData?.portalName,
    isIFPEarlyPay,
    isGuestUser,
    isEligibleForStandaloneDental
  );
  const isPolicyNumberRequired = getIsPolicyIDRequired(
    currentMember,
    isGuestUser
  );

  const necessaryParams: any = {
    ...(isPolicyNumberRequired ? { policyId } : {}),
    providerType,
    reciprocityId,
    coverageType,
    ...(planVariationCodeRequired ? { planVariationCode } : {}),
  };

  if (!isIFPEarlyPay) {
    necessaryParams.latitude = latitude;
    necessaryParams.longitude = longitude;
  }

  useEffect(() => {
    if (
      isSmartChoiceEnabled &&
      (preferenceData == null || preferenceData === undefined)
    ) {
      return;
    }
    if (isMissingParameters('ProviderDetails', necessaryParams)) {
      return;
    }

    if (!providerId) {
      return;
    }
    if (isProviderIdWithAddressId && providerId.includes('|')) {
      providerId = providerId.split('|')[0];
    }
    if (isProviderIdWithAddressId && providerId.includes(' ')) {
      providerId = providerId.split(' ')[0];
    }
    // If smart choice enabled and response is present from recommendation API
    if (
      isSmartChoiceEnabled &&
      !('llmCodes' in recommendationPreferences) &&
      !searchParams.get('choosePCPId')
    ) {
      return;
    }

    if (
      typeof setSelectedPCP !== 'undefined' &&
      demographics?.primaryCare?.providerID === providerId
    ) {
      setSelectedPCP(true);
    }

    const memCatergory = currentMember?.membershipCategory || '';
    const membershipCategory = conditionalValueRender(
      uspToggleFlag,
      memCatergory,
      null
    );

    getProviderDetails({
      variables: {
        ...(chipValue === CareCategories.FACILITIES
          ? { mockRecommendationForFacilities: isSmartChoice }
          : {}),
        latitude,
        longitude,
        stateCode,
        locale,
        lob,
        population,
        membershipCategory,
        coverages: getCoverageTypes(currentMember),
        planYear: getMemberPlanYear(isPreEffective, currentMemberEffectiveDate),
        memberEffectiveDate: isPreEffective ? currentMemberEffectiveDate : null,
        planVariationCode,
        claimSystemTypeCode: getClaimOrEligibilitySystemTypeCode(
          currentMember,
          coverageType
        )?.claimSystemTypeCode,
        eligibilitySystemTypeCode: getClaimOrEligibilitySystemTypeCode(
          currentMember,
          coverageType
        )?.eligibilitySystemTypeCode,
        policyId,
        policyID: policyId,
        providerId,
        providerType: providerType ?? ProviderType.PRACTITIONER,
        reciprocityId,
        rulesPackageKey: getRulesPackageKey(
          currentMember,
          tierBenefits,
          providerType,
          uspTierOneFlag,
          enableTier1FromMemberRPK
        ),
        snpType:
          coverageType == ReverseCoverageTypesCodes.MEDICAL
            ? medicalCoverage?.snpType
            : '',
        coverageType,
        locationId: locationId || undefined,
        carePathCategoriesSortOrder: getCarePathCategoriesSortOrder(
          ProviderType.PRACTITIONER === providerType,
          locale,
          costCareCategories,
          isMnr
        ),
        shouldGetHGData,
        suppressedVirtualVisitPolicyIds,
        isCustomPreferredPolicy,
        isFutureTerminationDemo,
        terminationProviderIdsDemo,
        cesProductCode: medicalCoverage?.productCode,
        memberMarketType: medicalCoverage?.marketType,
        shouldGetLFData: !!searchFromTypeAhead || !!category,
        preferredSpecialityCode,
        additionalAttributes: getAdditionalAttributes(
          additionalAttributes,
          currentMember,
          coverageType,
          providerType,
          providerType === ProviderType.PROVIDER_GROUP ||
            providerType === ProviderType.MEDICAL_GROUP
            ? GQL_API_NAMES.FACILITY_DETAILS
            : GQL_API_NAMES.PROVIDER_DETAILS
        ),
        enableMockProviderIdsForPreEffective,
        enableTierOneMockProviderIds: enableTierOneMockProviderIds || false,
        isIFPEarlyPay,
        futureStartDateProviderIds: getProviderIds(futureStartDateProviderIds),
        tierOneMockProviderIds: getProviderIds(tierOneMockProviderIds),
        acoMockProviderIds: getProviderIds(acoMockProviderIds),
        premiumCareMockProviderIds: getProviderIds(premiumCareMockProviderIds),
        mnrFutureMockProviderIds: getProviderIds(mnrFutureMockProviderIds),
        ...(shouldSuppressEAPFromDetailsPage && {
          suppressEAPOption: !enableEmployeeAssistanceProgramAcceptedFilter,
        }),
        sortBy: sortByValue,
        ...(isSmartChoiceEnabled && {
          recommendationPreferences,
          isProviderRecommendationsEnabled: true,
        }),
        virtualOnlyLocationInd,
        isGuestUser,
        suppressAOECodes: isPolicyIdPresentForAOESuppression(
          coverageType,
          suppressedAOECodesRow,
          currentMember?.policyNumber ?? ''
        ),
        requestAiBio,
        isNewPositionsForSmartChoiceEnabled,
        ...(isLawwPortal
          ? {}
          : { clientName: portalData?.clientName || ClientNames.PSX }),
      },
    });
  }, [
    latitude,
    longitude,
    stateCode,
    providerId,
    providerType,
    coverageType,
    reciprocityId,
    policyId,
    planVariationCode,
    locationId,
    networkId,
    isOHBS,
    JSON.stringify(recommendationPreferences),
    isSmartChoiceEnabled,
  ]);

  return providerDetails;
};
